import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useScroll, useThrottle } from 'ahooks';
import { Segment } from 'tea-component';
import { useIntl } from 'umi';
import useSlideEffect from '@intelligence/pages/TrendingGames/components/GameSelect/_hooks/useSlideEffect';
import Abnormal from '@home/components/Abnormal';
import SvgIcon from '@home/components/SvgIcon';
import LinkageTryPlayIcon from '@/pages/aigc/assets/svg/skill-detail-linkage-try-brainy.svg';
import type { ISkillData } from '../../../types';
import type { ISkillLinkage } from '../../../types/skillDetail';
import { useSkillLinkagesData } from '../../../hooks/useSkillLinkagesData';
import SkillDetailEmptyState from '../SkillDetailEmptyState';
import SkillLinkagePreview from '../SkillLinkagePreview';
import './index.scss';

export interface ISkillLinkageSectionProps {
  /** 当前 Skill 数据 */
  skill: ISkillData;
  /** 是否英文环境 */
  isEN: boolean;
  /** 是否启用数据请求 */
  enabled: boolean;
  /** 点击"试一下"回调，参数为提问示例文案 */
  onTryLinkage: (question: string) => void;
}

const PREFIX = 'dba-skill-linkage-section';
const SEGMENT_SCROLL_BTN_WIDTH = 24;
const SEGMENT_SCROLL_GAP = 4;

/**
 * 联动案例 Tab 内容
 */
const SkillLinkageSection: FC<ISkillLinkageSectionProps> = ({
  skill,
  isEN,
  enabled,
  onTryLinkage,
}) => {
  const { formatMessage } = useIntl();
  const scroll = useThrottle(useScroll(
    () => document.querySelector('.dba-skill-detail-tabs__panel'),
  ), { wait: 100 });
  const isToolbarStuck = (scroll?.top ?? 0) > 0;
  const { linkages, loading, error } = useSkillLinkagesData(skill.skill_id, enabled);
  const [activeLinkageId, setActiveLinkageId] = useState<number | null>(null);

  const displayLinkages = useMemo<ISkillLinkage[]>(
    () => [...linkages].sort((a, b) => a.sort - b.sort),
    [linkages],
  );

  useEffect(() => {
    setActiveLinkageId(null);
  }, [skill.skill_id, displayLinkages]);

  const activeLinkage = useMemo<ISkillLinkage | undefined>(() => {
    if (displayLinkages.length === 0) {
      return undefined;
    }
    if (activeLinkageId !== null) {
      return displayLinkages.find((item) => item.id === activeLinkageId);
    }
    return displayLinkages[0];
  }, [displayLinkages, activeLinkageId]);

  const segmentValue = useMemo(() => {
    if (activeLinkage) {
      return String(activeLinkage.id);
    }
    return '';
  }, [activeLinkage]);

  const segmentOptions = useMemo(
    () =>
      displayLinkages.map((item) => ({
        value: String(item.id),
        text: isEN ? item.title_en?.trim() || item.title : item.title,
      })),
    [displayLinkages, isEN],
  );

  const {
    containerRef,
    listContainerRef,
    isOverflow,
    slideDisabledState,
    handleSlideLeft,
    handleSlideRight,
  } = useSlideEffect({
    othersWidth: (SEGMENT_SCROLL_BTN_WIDTH + SEGMENT_SCROLL_GAP) * 2,
    updateDeps: [segmentOptions],
  });

  const handleTry = useCallback(() => {
    if (!activeLinkage) {
      return;
    }
    const question = isEN
      ? (activeLinkage.question_example_en?.trim()
        || activeLinkage.question_example?.trim()
        || '')
      : (activeLinkage.question_example?.trim()
        || activeLinkage.question_example_en?.trim()
        || '');
    if (!question) {
      return;
    }
    onTryLinkage(question);
  }, [activeLinkage, isEN, onTryLinkage]);

  const isLinkageEmpty = !loading && (Boolean(error) || displayLinkages.length === 0);
  const emptyMessage = formatMessage({
    id: error ? 'common_loading_failed' : 'dba_skill_detail_linkage_empty',
  });

  if (loading) {
    return (
      <div className={PREFIX}>
        <Abnormal
          isLoading
          style={{
            height: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
      </div>
    );
  }

  if (isLinkageEmpty) {
    return (
      <div className={classNames(PREFIX, `${PREFIX}--empty`)}>
        <SkillDetailEmptyState message={emptyMessage} />
      </div>
    );
  }

  return (
    <div className={PREFIX}>
      <div
        className={classNames(`${PREFIX}__toolbar`, {
          [`${PREFIX}__toolbar--stuck`]: isToolbarStuck,
        })}
      >
        <div className={`${PREFIX}__segment-slot`}>
          <div className={`${PREFIX}__segment-bar`} ref={containerRef}>
            {isOverflow && (
              <button
                type="button"
                className={classNames(`${PREFIX}__scroll-btn`, {
                  [`${PREFIX}__scroll-btn--disabled`]: slideDisabledState.prev,
                })}
                disabled={slideDisabledState.prev}
                onClick={handleSlideLeft}
                aria-label="scroll left"
              >
                <SvgIcon type="agent-left-arrow" />
              </button>
            )}

            <div className={`${PREFIX}__segment-content`} ref={listContainerRef}>
              <Segment
                className={`${PREFIX}__segment`}
                options={segmentOptions}
                value={segmentValue}
                onChange={(value) => setActiveLinkageId(Number(value))}
              />
            </div>

            {isOverflow && (
              <button
                type="button"
                className={classNames(`${PREFIX}__scroll-btn`, {
                  [`${PREFIX}__scroll-btn--disabled`]: slideDisabledState.next,
                })}
                disabled={slideDisabledState.next}
                onClick={handleSlideRight}
                aria-label="scroll right"
              >
                <SvgIcon type="agent-right-arrow" />
              </button>
            )}
          </div>
        </div>
        <button
          type="button"
          className={`${PREFIX}__try-btn`}
          onClick={handleTry}
        >
          <span className={`${PREFIX}__try-text`}>
            {formatMessage({ id: 'dba_skill_detail_linkage_try' })}
          </span>
          <img
            src={LinkageTryPlayIcon}
            alt=""
            className={`${PREFIX}__try-icon`}
          />
        </button>
      </div>

      {activeLinkage && (
        <SkillLinkagePreview
          activeLinkage={activeLinkage}
          isEN={isEN}
          enabled={enabled}
        />
      )}
    </div>
  );
};

export default memo(SkillLinkageSection);
